import {
  DialogueState,
  DialogueActions,
  DialogueStore,
  DialogueData,
  Listener,
  DialogueConfig,
  EffectRepeatConfig,
} from './types'
import { parseTextToSegments, getTotalLength, getDisplayText } from './utils'

/**
 * 创建对话状态管理器
 * 使用发布订阅模式,框架无关
 */
export const createDialogueStore = (
  initialConfig: DialogueConfig = {}
): DialogueStore & {
  subscribe: (listener: Listener) => () => void
  getState: () => DialogueState
} => {
  let state: DialogueState = {
    isVisible: false,
    currentDialogue: null,
    dialogueQueue: [],
    isTyping: false,
    displayText: '',
    currentTextLength: 0,
    displaySegments: [],
    hasCompleted: false,
    typewriterSpeed: initialConfig.typewriterSpeed ?? 50,
    effectRepeats: initialConfig.effectRepeats ?? {},
    onComplete: undefined,
  }

  let typewriterTimer: ReturnType<typeof setInterval> | null = null
  const listeners = new Set<Listener>()

  /**
   * 更新状态并通知监听器
   */
  const setState = (updates: Partial<DialogueState>) => {
    state = { ...state, ...updates }
    listeners.forEach((listener) => listener(state))
  }

  /**
   * 订阅状态变化
   */
  const subscribe = (listener: Listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  /**
   * 获取当前状态
   */
  const getState = () => state

  /**
   * 清除打字机定时器
   */
  const clearTypewriter = () => {
    if (typewriterTimer) {
      clearInterval(typewriterTimer)
      typewriterTimer = null
    }
  }

  /**
   * 开始打字机效果
   */
  const startTypewriter = () => {
    if (!state.currentDialogue || state.isTyping) return

    // 清除现有定时器
    clearTypewriter()

    // 解析文本为片段
    const segments = parseTextToSegments(state.currentDialogue.text)
    const totalLength = getTotalLength(segments)

    setState({
      isTyping: true,
      hasCompleted: false,
      currentTextLength: 0,
      displaySegments: segments,
      displayText: '',
    })

    // 启动打字机定时器
    typewriterTimer = setInterval(() => {
      const currentState = getState()
      const newLength = currentState.currentTextLength + 1
      const isComplete = newLength >= totalLength

      if (isComplete) {
        setState({
          currentTextLength: totalLength,
          displayText: getDisplayText(segments, totalLength),
          hasCompleted: true,
          isTyping: false,
        })
        clearTypewriter()
        return
      }

      setState({
        currentTextLength: newLength,
        displayText: getDisplayText(segments, newLength),
      })
    }, state.typewriterSpeed)
  }

  /**
   * 设置特效循环次数
   */
  const setEffectRepeats = (config: EffectRepeatConfig) => {
    setState({ effectRepeats: { ...state.effectRepeats, ...config } })
  }

  /**
   * 处理点击:跳过打字或推进对话
   */
  const handleClick = () => {
    if (state.isTyping) {
      // 跳过到完整文本
      clearTypewriter()
      const totalLength = getTotalLength(state.displaySegments)
      setState({
        currentTextLength: totalLength,
        displayText: getDisplayText(state.displaySegments, totalLength),
        hasCompleted: true,
        isTyping: false,
      })
    } else if (state.hasCompleted) {
      // 推进到下一句对话
      nextDialogue()
    }
  }

  /**
   * 显示对话条目
   */
  const showDialogue = (dialogue: DialogueData) => {
    const segments = parseTextToSegments(dialogue.text)

    // 避免重复显示
    if (state.currentDialogue?.text === dialogue.text) {
      return
    }

    clearTypewriter()

    setState({
      isVisible: true,
      currentDialogue: dialogue,
      displaySegments: segments,
      currentTextLength: 0,
      isTyping: false,
      hasCompleted: false,
    })

    // 延迟启动打字机,确保状态已更新
    setTimeout(() => startTypewriter(), 0)
  }

  /**
   * 隐藏对话
   */
  const hideDialogue = () => {
    clearTypewriter()
    setState({
      isVisible: false,
      currentDialogue: null,
      isTyping: false,
      displaySegments: [],
      currentTextLength: 0,
      hasCompleted: false,
    })
  }

  /**
   * 推进到队列中的下一句对话
   */
  const nextDialogue = () => {
    if (state.dialogueQueue.length > 0) {
      const nextDialogue = state.dialogueQueue[0]
      setState({ dialogueQueue: state.dialogueQueue.slice(1) })
      showDialogue(nextDialogue)
    } else {
      hideDialogue()
      state.onComplete?.()
    }
  }

  /**
   * 添加多句对话到队列
   */
  const queueDialogues = (dialogues: DialogueData[], showFirst = true) => {
    if (showFirst && dialogues.length > 0) {
      setState({ dialogueQueue: dialogues.slice(1) })
      showDialogue(dialogues[0])
    } else {
      setState({ dialogueQueue: dialogues })
    }
  }

  /**
   * 设置完成回调
   */
  const setOnComplete = (callback: () => void) => {
    setState({ onComplete: callback })
  }

  /**
   * 开始预设对话
   */
  const startDialogue = (dialogues: DialogueData[], onComplete?: () => void) => {
    if (onComplete) {
      setOnComplete(onComplete)
    }
    queueDialogues(dialogues, true)
  }

  /**
   * 设置打字机速度
   */
  const setTypewriterSpeed = (speed: number) => {
    setState({ typewriterSpeed: speed })
  }

  const actions: DialogueActions = {
    startTypewriter,
    clearTypewriter,
    handleClick,
    nextDialogue,
    showDialogue,
    hideDialogue,
    queueDialogues,
    setOnComplete,
    startDialogue,
    setTypewriterSpeed,
    setEffectRepeats,
  }

  return {
    ...state,
    ...actions,
    subscribe,
    getState,
  }
}

